import { useEffect, useState } from 'react';
import { IoFlagOutline } from 'react-icons/io5';
import { useAnnualPlanTranslation } from '../../hooks/useAnnualPlanTranslation';
import { atpApi, type ATPObjective, type ATPWeek } from '../../services/atpApi';
import { calendarWeeklyObjectives } from '../../utils/calendarWeeklyObjectives';
import { displayDigits } from '../../utils/displayDigits';
import { formatIsoDateLabel } from '../../i18n/formatters';
import './calendar-weekly-goals.css';

export function CalendarWeeklyGoals({ userId, dates, refreshToken, weeks: suppliedWeeks, objectives: suppliedObjectives }: {
  userId: number | null; dates: string[]; refreshToken?: unknown; weeks?: ATPWeek[]; objectives?: ATPObjective[];
}) {
  const { t, i18n } = useAnnualPlanTranslation();
  const [loaded, setLoaded] = useState<{ userId: number; weeks: ATPWeek[]; objectives: ATPObjective[] } | null>(null);
  useEffect(() => {
    if (suppliedObjectives !== undefined || !userId) return;
    let live = true;
    setLoaded(null);
    void atpApi.getAtp(userId).then(result => {
      if (live) setLoaded({ userId, weeks: result?.weeks ?? [], objectives: result?.atp.monitoring?.objectives ?? [] });
    }).catch(() => {});
    return () => { live = false; };
  }, [userId, refreshToken, suppliedObjectives]);
  const active = loaded?.userId === userId ? loaded : null;
  const groups = calendarWeeklyObjectives(dates, suppliedWeeks ?? active?.weeks ?? [], suppliedObjectives ?? active?.objectives ?? []);
  if (!userId || !groups.length) return null;
  const text = (value: string) => displayDigits(value, i18n.language);
  return <section className="calendar-weekly-goals" aria-label={t('annualPlan.calendarGoals.title')}>
    <h3><IoFlagOutline aria-hidden="true" />{t('annualPlan.calendarGoals.title')}</h3>
    <div className="calendar-weekly-goals__weeks">{groups.map(group => <article key={group.start}>
      <h4><time dateTime={group.start}>{text(formatIsoDateLabel(group.start, i18n.language))}</time> – <time dateTime={group.end}>{text(formatIsoDateLabel(group.end, i18n.language))}</time></h4>
      <ul>{group.goals.map(goal => <li key={goal.id}>
        <details><summary className="calendar-weekly-goals__heading"><strong>{text(goal.title)}</strong>
          <span>{t(goal.scope === 'phase' ? 'annualPlan.calendarGoals.phase' : 'annualPlan.calendarGoals.week')}</span>
          <span>{t('annualPlan.monitor.' + goal.status)}</span></summary>
        {goal.criterion && <p>{text(goal.criterion)}</p>}</details>
      </li>)}</ul>
    </article>)}</div>
  </section>;
}
